import React, { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { Home, Search, Heart, ShoppingBag, Crown, Globe, LogIn, LogOut, ChefHat, CalendarRange, Activity, Sun, Moon, LifeBuoy, ShieldCheck, Flame, Trash2 } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { api } from '../lib/api';
import { toast } from 'sonner';

const NAV = [
  { to: '/', icon: Home, key: 'discover' },
  { to: '/search', icon: Search, key: 'search' },
  { to: '/trending', icon: Flame, key: 'trending' },
  { to: '/week-plan', icon: CalendarRange, key: 'weekPlan' },
  { to: '/tracker', icon: Activity, key: 'tracker' },
  { to: '/favorites', icon: Heart, key: 'favorites' },
  { to: '/shopping', icon: ShoppingBag, key: 'shopping' },
];

export default function Layout() {
  const { t, lang, setLang, languages, isRTL } = useLang();
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const loc = useLocation();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete('/auth/account');
      toast.success(t('accountDeleted'));
      // Clear local session and redirect
      try { localStorage.removeItem('matbakhi_session_token'); } catch { /* noop */ }
      await logout().catch(() => { /* user is already gone server-side */ });
      navigate('/login', { replace: true });
    } catch (e) {
      console.error('[delete-account]', e);
      toast.error(t('deleteAccountFailed'));
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div className="min-h-screen pb-32 bg-[#F9F6F0] dark:bg-neutral-950 text-[#2C2420] dark:text-neutral-100 transition-colors" style={{ paddingBottom: 'calc(8rem + env(safe-area-inset-bottom))' }}>
      {/* Top header */}
      <header className="sticky top-0 z-30 glass dark:glass-dark border-b border-black/5 dark:border-white/10" style={{ paddingTop: 'max(8px, env(safe-area-inset-top))' }}>
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 group" data-testid="brand-link">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-[#C05A42] text-white shadow-sm group-hover:rotate-3 transition-transform">
              <ChefHat className="h-5 w-5" />
            </span>
            <span className="text-lg font-display font-bold tracking-tight">{t('appName')}</span>
          </Link>

          <div className="flex-1" />

          {/* Premium / Trial CTA — visible to everyone so users can discover Premium */}
          <Link
            to="/premium"
            data-testid="premium-link"
            aria-label={t('premium') || 'Premium'}
            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-semibold transition shadow-sm ${
              user?.is_premium
                ? 'bg-[#7A8B76] text-white hover:bg-[#62725f]'
                : 'bg-[#E29F48] text-white hover:bg-[#C9893A]'
            }`}
          >
            <Crown className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{user?.is_premium ? t('premium') || 'Premium' : (t('tryFree') || 'تجربة مجانية')}</span>
          </Link>

          <button
            type="button"
            onClick={toggleTheme}
            data-testid="theme-toggle"
            aria-label={theme === 'dark' ? t('lightMode') : t('darkMode')}
            className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-white dark:bg-neutral-800 border border-black/5 dark:border-white/10 hover:bg-[#EFEBE1] dark:hover:bg-neutral-700 transition"
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="language-trigger"
              className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium bg-white dark:bg-neutral-800 border border-black/5 dark:border-white/10 hover:bg-[#EFEBE1] dark:hover:bg-neutral-700 transition"
            >
              <Globe className="h-4 w-4" />
              <span className="hidden sm:inline">{(languages.find(l => l.code === lang) || {}).label}</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={isRTL ? 'start' : 'end'} className="min-w-[160px]">
              <DropdownMenuLabel>{t('language')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {languages.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  data-testid={`lang-${l.code}`}
                  className={lang === l.code ? 'bg-[#EFEBE1] dark:bg-neutral-700' : ''}
                >
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user && !user.is_premium && (
            <Link
              to="/premium"
              data-testid="header-premium-link"
              className="hidden sm:inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm font-medium bg-[#E29F48] text-white hover:bg-[#cd8c33] transition shadow-sm"
            >
              <Crown className="h-4 w-4" /> {t('premium')}
            </Link>
          )}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu" className="rounded-full">
                <Avatar className="h-9 w-9 ring-2 ring-white dark:ring-neutral-800 shadow-sm">
                  <AvatarImage src={user.picture} alt={user.name} />
                  <AvatarFallback>{(user.name || user.email || '?')[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align={isRTL ? 'start' : 'end'} className="min-w-[200px]">
                <DropdownMenuLabel className="max-w-[200px] truncate">{user.name || user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="menu-privacy">
                  <Link to="/privacy">
                    <ShieldCheck className="me-2 h-4 w-4" />
                    {t('privacyPolicy')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="menu-support">
                  <a href="mailto:info@matbakhi.app?subject=Matbakhi%20Support">
                    <LifeBuoy className="me-2 h-4 w-4" />
                    {t('support')}
                  </a>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                  <LogOut className="me-2 h-4 w-4" />
                  {t('logout')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setConfirmDelete(true)}
                  data-testid="delete-account-btn"
                  className="text-red-600 dark:text-red-400 focus:text-red-700 focus:bg-red-50 dark:focus:bg-red-950/40"
                >
                  <Trash2 className="me-2 h-4 w-4" />
                  {t('deleteAccount')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link
              to="/login"
              data-testid="header-login-link"
              className="inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium bg-[#C05A42] text-white hover:bg-[#A04832] transition shadow-sm"
            >
              <LogIn className="h-4 w-4" />
              <span>{t('login')}</span>
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-6">
        <Outlet />
      </main>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 z-30 glass dark:glass-dark border-t border-black/5 dark:border-white/10" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
        <div className="max-w-5xl mx-auto px-2 py-2 flex items-center justify-around overflow-x-auto no-scrollbar">
          {NAV.map(({ to, icon: Icon, key }) => {
            const active = to === '/' ? loc.pathname === '/' : loc.pathname.startsWith(to);
            return (
              <Link
                key={to}
                to={to}
                data-testid={`nav-${key}`}
                className={`flex flex-col items-center gap-1 px-3 py-1.5 rounded-2xl text-[11px] font-medium transition shrink-0 ${
                  active ? 'text-[#C05A42] bg-[#EFEBE1] dark:bg-neutral-800' : 'text-[#5C4D44] dark:text-neutral-400 hover:text-[#C05A42]'
                }`}
              >
                <Icon className={`h-5 w-5 ${active ? 'scale-110' : ''} transition-transform`} />
                <span>{t(key)}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Delete-account confirmation dialog (Google Play user-deletion compliance) */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="delete-account-dialog" className={isRTL ? 'rtl' : 'ltr'}>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 dark:text-red-400">
              {t('deleteAccountTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>{t('deleteAccountDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-account-cancel">
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={deleting}
              data-testid="delete-account-confirm"
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? '…' : t('deleteAccountConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
